/**
 * Assistant chat context, scoped per flow step ("phase").
 *
 * Pulled out of `assistant-sheet.tsx` so the phase-transition rule and the
 * per-step prompts are pure and unit-testable on their own, the same way the
 * backend's `knowledge.py` keeps its step-scoping pure and separate from the
 * OpenAI call in `assistant.py`.
 */

import { PRODUCT_NAMES } from "./product-names";

const DEFAULT_SUGGESTIONS = [
  "What should I do on this screen?",
  "Something isn't connecting. Can you check?",
  "How long should this take?",
];

/**
 * Suggested starting points per flow step, so a fresh phase gets prompts about
 * *this* step rather than three generic ones. Scoped the same way the
 * backend's `knowledge.py` already scopes GUIDE entries, and grounded in the
 * same known failure modes, so a tap actually lands on something the assistant
 * can speak to.
 *
 * `"guide"` mirrors the backend's `HOME_SCREEN_KEY` sentinel (flow.py) for the
 * Home Hub overview, which isn't itself a flow step.
 */
const SUGGESTIONS_BY_STEP: Record<string, string[]> = {
  welcome: [
    "What do I need before I start?",
    "How long will setup take?",
    "Something isn't connecting. Can you check?",
  ],
  kit_confirmation: [
    "A part in my kit looks like it's missing",
    "How do I match parts to compartments?",
    "What if something looks damaged?",
  ],
  network_setup: [
    "My WiFi won't connect",
    "The network switch has no lights on",
    "What network does the hub need?",
  ],
  home_assistant: [
    "The hub's light is solid but setup won't move on",
    "My hub won't come online",
    "How long should this step take?",
  ],
  air_sensors: [
    "A sensor has never sent a reading",
    "Where should I place a sensor?",
    "How do I know a sensor is working?",
  ],
  floor_heating: [
    "The gateway won't finish setting up",
    "A heating zone shows the wrong room",
    "How long should this step take?",
  ],
  ventilation: [
    "The gateway won't finish setting up",
    "My ventilation unit isn't showing up",
    "How long should this step take?",
  ],
  windows: [
    "Nothing moved when I tried a window",
    `The ${PRODUCT_NAMES.touchRemote} counted fewer windows than expected`,
    "How long should this step take?",
  ],
  complete: [
    "What do I do if something stops working later?",
    "What happens with the problems I skipped?",
    "Can I change a setting later?",
  ],
  guide: [
    "What should I do next?",
    "How long is left?",
    "Something isn't connecting. Can you check?",
  ],
};

/** Suggested prompts for a step, falling back to generic ones off the flow. */
export function suggestionsForStep(stepKey: string | null | undefined): string[] {
  return (stepKey && SUGGESTIONS_BY_STEP[stepKey]) || DEFAULT_SUGGESTIONS;
}

/**
 * Whether opening the assistant for `nextStepKey` starts a new phase, given
 * the step the active thread is currently scoped to.
 *
 * `undefined` and `null` both mean "no step scope" and compare equal, so
 * opening the sheet twice without a step (or twice with the same step) never
 * looks like a phase change.
 */
export function isNewPhase(
  activePhaseKey: string | null,
  nextStepKey: string | null | undefined,
): boolean {
  return (nextStepKey ?? null) !== activePhaseKey;
}
